import { getLocale, translate } from '../i18n'
import { hideSyncBanner, showSyncBanner, syncBannerState } from '../state/appUiStore'

export type SyncFailureState = {
   failureCount: number
   lastFailureTimestamp: number
   nextRetryAt: number
}

type SyncFailureTrackerDeps = {
   syncRetryIntervalMs: number
   canUseRemoteSync: () => boolean
   getUserId: () => string
   getSyncDebugInfo: () => { lastPersist: unknown }
   persistDebugInfo: () => void
   syncAccountStateToSolid: () => void
}

/**
 * Formats the last failed-attempt time for the failure banner.
 *
 * @param lastSyncFailureTimestamp - The last failure time in epoch ms.
 * @returns The localized time string.
 */
function formatLastAttemptTime(lastSyncFailureTimestamp: number) {
   return new Date(lastSyncFailureTimestamp).toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
   })
}

/**
 * Shows the delayed-sync banner describing the failure and next retry.
 *
 * @param syncFailureCount - Consecutive failures so far.
 * @param lastSyncFailureTimestamp - The last failure time in epoch ms.
 * @param delayMs - Delay until the next retry, in milliseconds.
 */
function showSyncFailureBanner(
   syncFailureCount: number,
   lastSyncFailureTimestamp: number,
   delayMs: number,
) {
   const attempt = Math.max(syncFailureCount, 1)
   const seconds =
      Number.isFinite(delayMs) && delayMs > 0 ?
         Math.max(1, Math.round(delayMs / 1000))
      :  null
   let detail =
      attempt > 1 ?
         translate('Attempt {attempt} failed.', { attempt })
      :  translate('Latest sync attempt failed.')
   if (seconds !== null) {
      const unitKey = seconds === 1 ? 'second' : 'seconds'
      detail += ` ${translate('Retrying in {seconds} {unit}.', {
         seconds,
         unit: translate(unitKey),
      })}`
   } else {
      detail += ` ${translate('Retrying shortly.')}`
   }
   let timestamp = ''
   if (lastSyncFailureTimestamp) {
      timestamp = translate('Last attempt {time}', {
         time: formatLastAttemptTime(lastSyncFailureTimestamp),
      })
   }
   showSyncBanner({
      message: translate('Cloud sync delayed'),
      detail,
      timestamp,
   })
}

/**
 * Updates the sync banner to indicate a retry is in progress.
 *
 * @param syncFailureCount - Consecutive failures so far.
 * @param lastSyncFailureTimestamp - The last failure time in epoch ms.
 */
function setSyncBannerRetryingNow(
   syncFailureCount: number,
   lastSyncFailureTimestamp: number,
) {
   const attempt = Math.max(syncFailureCount + 1, 2)
   const detail = translate('Retrying sync now (attempt {attempt})...', {
      attempt,
   })
   const timestamp =
      lastSyncFailureTimestamp ?
         `Last attempt ${formatLastAttemptTime(lastSyncFailureTimestamp)}`
      :  ''
   showSyncBanner({
      message: translate('Cloud sync delayed'),
      detail,
      timestamp,
   })
}

/**
 * Tracks consecutive sync failures, drives the retry timer, and keeps the
 * "cloud sync delayed" banner in step with it.
 *
 * @param deps - Identity/config accessors and debug-info persistence.
 * @param retry - Re-runs a sync for the given action.
 * @returns The failure-tracking API.
 */
export function createSyncFailureTracker(
   deps: SyncFailureTrackerDeps,
   retry: (action: string) => void,
) {
   let syncRetryTimerId: number | null = null
   let syncFailureCount = 0
   let lastSyncFailureAction: string | null = null
   let lastSyncFailureTimestamp = 0
   let syncNextRetryAt = 0

   /**
    * Clears any pending sync-retry timer.
    */
   function clearSyncRetryTimer() {
      if (syncRetryTimerId !== null) {
         window.clearTimeout(syncRetryTimerId)
         syncRetryTimerId = null
      }
   }

   /**
    * Resets all sync-failure tracking and hides the banner.
    */
   function clearSyncFailureState() {
      syncFailureCount = 0
      lastSyncFailureAction = null
      lastSyncFailureTimestamp = 0
      syncNextRetryAt = 0
      clearSyncRetryTimer()
      hideSyncBanner()
   }

   /**
    * Clears stored last-persist metadata that may be causing repeated conflicts.
    */
   function clearStaleSyncMetadata() {
      deps.getSyncDebugInfo().lastPersist = null
      deps.persistDebugInfo()
   }

   /**
    * Schedules a delayed sync retry for the given action.
    *
    * @param action - The action to retry, or null to reuse the last failed action.
    * @param delayMs - Delay before retrying (defaults to the configured interval).
    */
   function scheduleSyncRetry(action: string | null, delayMs = deps.syncRetryIntervalMs) {
      if (!Number.isFinite(delayMs) || delayMs <= 0) {
         delayMs = deps.syncRetryIntervalMs
      }
      clearSyncRetryTimer()
      syncNextRetryAt = Date.now() + delayMs
      syncRetryTimerId = window.setTimeout(() => {
         syncRetryTimerId = null
         if (!deps.getUserId() || !deps.canUseRemoteSync()) {
            return
         }
         setSyncBannerRetryingNow(syncFailureCount, lastSyncFailureTimestamp)
         retry(action || lastSyncFailureAction || 'sync-retry')
      }, delayMs)
   }

   /**
    * Records a sync failure and schedules a retry with a failure banner.
    *
    * @param action - The action that failed.
    */
   function handleSyncFailure(action: string) {
      if (!deps.getUserId() || !deps.canUseRemoteSync()) {
         return
      }
      lastSyncFailureAction = action || lastSyncFailureAction || 'sync'
      syncFailureCount = Math.max(syncFailureCount + 1, 1)
      lastSyncFailureTimestamp = Date.now()
      showSyncFailureBanner(
         syncFailureCount,
         lastSyncFailureTimestamp,
         deps.syncRetryIntervalMs,
      )
      scheduleSyncRetry(lastSyncFailureAction, deps.syncRetryIntervalMs)
      deps.syncAccountStateToSolid()
   }

   /**
    * Clears failure state after a successful sync (no-op when already clear).
    */
   function handleSyncSuccess() {
      if (
         syncFailureCount === 0 &&
         syncRetryTimerId === null &&
         !syncBannerState().visible
      ) {
         return
      }
      clearSyncFailureState()
   }

   /**
    * Cancels the pending retry timer and retries the sync immediately.
    */
   function triggerImmediateSyncRetry() {
      if (!deps.getUserId() || !deps.canUseRemoteSync()) {
         return
      }
      clearSyncRetryTimer()
      syncNextRetryAt = Date.now()
      setSyncBannerRetryingNow(syncFailureCount, lastSyncFailureTimestamp)
      retry(lastSyncFailureAction || 'sync-retry')
   }

   /**
    * Returns the current sync-failure state snapshot.
    *
    * @returns The failure count and retry timestamps.
    */
   function getFailureState(): SyncFailureState {
      return {
         failureCount: syncFailureCount,
         lastFailureTimestamp: lastSyncFailureTimestamp,
         nextRetryAt: syncNextRetryAt,
      }
   }

   return {
      clearStaleSyncMetadata,
      clearSyncFailureState,
      getFailureState,
      handleSyncFailure,
      handleSyncSuccess,
      triggerImmediateSyncRetry,
   }
}
